<template>
    <div class="all-orders-admin">

        <!-- ── Page Header ── -->
        <div class="d-flex align-items-center justify-content-between mb-4">
            <h3 class="mb-0">Order Admin</h3>
            <b-button variant="primary" @click="refreshPage" :disabled="isLoading">
                <font-awesome-icon :icon="isLoading ? 'spinner' : 'sync'" :spin="isLoading" class="mr-1"/>
                Refresh
            </b-button>
        </div>

        <!-- ── Stats Row ── -->
        <b-row class="mb-4">
            <b-col sm="6" lg>
                <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                    <h4 class="mb-0 font-weight-bold">
                        <span class="stat-icon text-primary mb-2 mr-2"><font-awesome-icon icon="receipt"/></span>
                        <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                        <template v-else>{{ stats.orderCount }}</template>
                    </h4>
                    <small class="text-muted">Total Orders</small>
                </b-card>
            </b-col>
            <b-col sm="6" lg>
                <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                    <h4 class="mb-0 font-weight-bold">
                        <span class="stat-icon text-success mb-2 mr-2"><font-awesome-icon icon="dollar-sign"/></span>
                        <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                        <template v-else>{{ stats.totalRevenue | currency }}</template>
                    </h4>
                    <small class="text-muted">Total Revenue</small>
                </b-card>
            </b-col>
            <b-col sm="6" lg>
                <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                    <h4 class="mb-0 font-weight-bold">
                        <span class="stat-icon text-info mb-2 mr-2"><font-awesome-icon icon="chart-bar"/></span>
                        <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                        <template v-else>{{ stats.avgOrderValue | currency }}</template>
                    </h4>
                    <small class="text-muted">Avg. Order Value</small>
                </b-card>
            </b-col>
            <b-col sm="6" lg>
                <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                    <h4 class="mb-0 font-weight-bold">
                        <span class="stat-icon text-warning mb-2 mr-2"><font-awesome-icon icon="home"/></span>
                        <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                        <template v-else>{{ stats.homeschoolCount }}</template>
                    </h4>
                    <small class="text-muted">Homeschool Orders</small>
                </b-card>
            </b-col>
            <b-col sm="6" lg>
                <b-card class="stat-card text-center border-0 shadow-sm h-100 p-0" body-class="p-0">
                    <h4 class="mb-0 font-weight-bold">
                        <span class="stat-icon text-purple mb-2 mr-2"><font-awesome-icon icon="box"/></span>
                        <font-awesome-icon v-if="isStatsLoading" icon="spinner" spin/>
                        <template v-else>{{ stats.totalUnits }}</template>
                    </h4>
                    <small class="text-muted">Total Units</small>
                </b-card>
            </b-col>
        </b-row>

        <!-- ── Filters Card ── -->
        <b-card class="filter-card border-0 shadow-sm mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="filter-section-label">Filters</span>
                <b-button variant="link" size="sm" class="p-0 text-muted" @click="resetFilters" v-if="hasActiveFilters">
                    <font-awesome-icon icon="times" class="mr-1"/>Clear all
                </b-button>
            </div>

            <!-- Date Range -->
            <b-row class="mb-3">
                <b-col cols="12">
                    <div class="filter-section-label mb-2">Date Range</div>
                </b-col>
                <b-col sm="4" class="mb-2">
                    <b-form-group class="mb-0" label="Preset" label-size="sm">
                        <b-form-select size="sm" v-model="datePreset" :options="datePresetOptions" @change="applyDatePreset"/>
                    </b-form-group>
                </b-col>
                <b-col sm="4" class="mb-2">
                    <b-form-group class="mb-0" label="From" label-size="sm">
                        <b-form-input size="sm" type="date" v-model="filters.dateFrom" @change="datePreset = 'custom'"/>
                    </b-form-group>
                </b-col>
                <b-col sm="4" class="mb-2">
                    <b-form-group class="mb-0" label="To" label-size="sm">
                        <b-form-input size="sm" type="date" v-model="filters.dateTo" @change="datePreset = 'custom'"/>
                    </b-form-group>
                </b-col>
            </b-row>

            <hr class="my-3"/>

            <!-- Quick Filters -->
            <div class="filter-section-label mb-2">Quick Filters</div>
            <div class="d-flex flex-wrap mb-3" style="gap:.5rem;">
                <b-button
                    v-for="qf in quickFilters"
                    :key="qf.key"
                    :variant="activeQuickFilters.includes(qf.key) ? 'primary' : 'outline-secondary'"
                    size="sm"
                    @click="toggleQuickFilter(qf.key)"
                    class="quick-filter-btn"
                >
                    <font-awesome-icon :icon="qf.icon" class="mr-1"/>{{ qf.label }}
                </b-button>
            </div>

            <hr class="my-3"/>

            <!-- Search & Select Filters -->
            <b-row>
                <b-col md="4" lg="3" class="mb-2">
                    <b-form-group class="mb-0" label="Search" label-size="sm">
                        <b-input-group size="sm">
                            <b-input-group-prepend is-text><font-awesome-icon icon="search"/></b-input-group-prepend>
                            <b-form-input v-model="filters.search" placeholder="Email, name, PO, SKU…" debounce="300"/>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col md="4" lg="2" class="mb-2">
                    <b-form-group class="mb-0" label="Grade" label-size="sm">
                        <b-form-select size="sm" v-model="filters.grade" :options="gradeOptions"/>
                    </b-form-group>
                </b-col>
                <b-col md="4" lg="2" class="mb-2">
                    <b-form-group class="mb-0" label="Topic / Subject" label-size="sm">
                        <b-form-select size="sm" v-model="filters.topic" :options="topicOptions"/>
                    </b-form-group>
                </b-col>
                <b-col md="4" lg="2" class="mb-2">
                    <b-form-group class="mb-0" label="School Year" label-size="sm">
                        <b-form-select size="sm" v-model="filters.schoolYear" :options="schoolYearOptions"/>
                    </b-form-group>
                </b-col>
                <b-col md="4" lg="2" class="mb-2">
                    <b-form-group class="mb-0" label="State" label-size="sm">
                        <b-form-select size="sm" v-model="filters.state" :options="stateOptions"/>
                    </b-form-group>
                </b-col>
                <b-col md="4" lg="1" class="mb-2 d-flex align-items-end">
                    <b-button size="sm" variant="outline-secondary" block @click="resetFilters" v-b-tooltip.hover title="Clear filters">
                        <font-awesome-icon icon="times"/>
                    </b-button>
                </b-col>
            </b-row>

            <!-- Active filter chips -->
            <div class="mt-2 d-flex flex-wrap" style="gap:.35rem;" v-if="hasActiveFilters">
                <b-badge
                    v-for="chip in filterChips"
                    :key="chip.key"
                    variant="primary"
                    class="filter-chip"
                    @click="removeChip(chip)"
                >
                    {{ chip.label }} <font-awesome-icon icon="times" class="ml-1"/>
                </b-badge>
            </div>
        </b-card>

        <!-- ── Orders Table ── -->
        <b-card class="border-0 shadow-sm">
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap" style="gap:.5rem;">
                <small class="text-muted">
                    Showing {{ pageStart }}–{{ pageEnd }} of {{ totalCount }} orders
                </small>
                <b-form-select
                    v-model="perPage"
                    :options="perPageOptions"
                    size="sm"
                    style="width:auto;"
                    @change="currentPage = 1"
                />
            </div>

            <div v-if="isLoading" class="text-center py-5">
                <b-spinner variant="primary"/><br/>
                <span class="text-muted mt-2 d-block">Loading orders…</span>
            </div>
            <OrderCollection v-else :orders="allOrders" disable-pagination/>

            <div class="d-flex align-items-center justify-content-between mt-3 flex-wrap" style="gap:.5rem;">
                <small class="text-muted">Page {{ currentPage }} of {{ totalPages }}</small>
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalCount"
                    :per-page="perPage"
                    size="sm"
                    limit="7"
                    first-number
                    last-number
                    class="mb-0"
                />
            </div>
        </b-card>

    </div>
</template>

<script>
import OrderCollection from "@/components/dashboard/OrderCollection";
import states from "@/common/states";

export default {
    name: "AllOrders",
    components: { OrderCollection },

    data() {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const todayStr = `${yyyy}-${mm}-${dd}`;

        let perPage = parseInt(localStorage.getItem("orders_per_page") || '25');

        // Default: this calendar year
        const yearStart = `${yyyy}-01-01`;

        return {
            isLoading: false,
            isStatsLoading: false,
            allOrders: [],

            stats: {
                orderCount: 0,
                totalRevenue: 0,
                avgOrderValue: 0,
                homeschoolCount: 0,
                totalUnits: 0,
            },

            datePreset: 'this_year',
            filters: {
                search: '',
                dateFrom: yearStart,
                dateTo: todayStr,
                grade: null,
                topic: null,
                schoolYear: null,
                state: null,
            },

            activeQuickFilters: [],

            perPage: perPage,
            totalCount: 0,
            currentPage: 1,
            perPageOptions: [
                { value: 10,  text: '10 / page' },
                { value: 25,  text: '25 / page' },
                { value: 50,  text: '50 / page' },
                { value: 100, text: '100 / page' },
            ],

            quickFilters: [
                { key: 'homeschool', label: 'Homeschool',    icon: 'home' },
                { key: 'print',      label: 'Print',         icon: 'print' },
                { key: 'online',     label: 'Online Only',   icon: 'laptop' },
                { key: 'pending',    label: 'Pending',       icon: 'clock' },
                { key: 'delivered',  label: 'Delivered',     icon: 'check-circle' },
            ],
        };
    },

    computed: {
        datePresetOptions() {
            const y = new Date().getFullYear();
            let uniqueYears = _.uniq(this.allOrders.map(order => order.school_year));
            let ranges = [
                { value: 'all',        text: 'All Time' },
                { value: 'today',      text: 'Today' },
                { value: 'this_week',  text: 'This Week' },
                { value: 'this_month', text: 'This Month' },
                { value: 'last_month', text: 'Last Month' },
                { value: 'this_year',  text: `${y} (YTD)` },
                { value: 'custom',     text: 'Custom Range' },
            ];
            for (let year of uniqueYears) {
                if (year !== y)
                    ranges.push({ value: `${year}`, text: year });
            }
            return ranges;
        },

        gradeOptions() {
            const grades = [...new Set(
                this.allOrders.flatMap(o => (o.items || []).map(i => i.grade).filter(Boolean))
            )].sort((a, b) => Number(a) - Number(b));
            return [
                { value: null, text: 'All Grades' },
                ...grades.map(g => ({ value: g, text: `Grade ${g}` })),
            ];
        },

        topicOptions() {
            const topics = [...new Set(
                this.allOrders.flatMap(o => (o.items || []).map(i => i.subject || i.topic).filter(Boolean))
            )].sort();
            return [
                { value: null, text: 'All Topics' },
                ...topics.map(t => ({ value: t, text: t })),
            ];
        },

        schoolYearOptions() {
            const years = [...new Set(
                this.allOrders.flatMap(o => (o.items || []).map(i => i.school_year).filter(Boolean))
            )].sort().reverse();
            return [
                { value: null, text: 'All School Years' },
                ...years.map(y => ({ value: y, text: y })),
            ];
        },

        stateOptions() {
            return [
                { value: null, text: 'All States' },
                ...states.map(s => ({ value: s.state, text: `${s.state} — ${s.fullState}` })),
            ];
        },

        hasActiveFilters() {
            return this.filters.search ||
                this.filters.grade ||
                this.filters.topic ||
                this.filters.schoolYear ||
                this.filters.state ||
                this.activeQuickFilters.length ||
                this.datePreset !== 'all';
        },

        filterChips() {
            const chips = [];
            if (this.filters.search)     chips.push({ key: 'search',     label: `"${this.filters.search}"` });
            if (this.filters.grade)      chips.push({ key: 'grade',      label: `Grade ${this.filters.grade}` });
            if (this.filters.topic)      chips.push({ key: 'topic',      label: this.filters.topic });
            if (this.filters.schoolYear) chips.push({ key: 'schoolYear', label: this.filters.schoolYear });
            if (this.filters.state)      chips.push({ key: 'state',      label: this.filters.state });
            this.activeQuickFilters.forEach(k => {
                const qf = this.quickFilters.find(q => q.key === k);
                if (qf) chips.push({ key: `quick_${k}`, label: qf.label, quickKey: k });
            });
            return chips;
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.totalCount / this.perPage));
        },

        pageStart() {
            if (!this.totalCount) return 0;
            return (this.currentPage - 1) * this.perPage + 1;
        },

        pageEnd() {
            return Math.min(this.currentPage * this.perPage, this.totalCount);
        },
    },

    watch: {
        perPage() {
            this.loadOrders();
            localStorage.setItem('orders_per_page', this.perPage);
        },
        currentPage() {
            this.loadOrders();
        },
        filters: {
            deep: true,
            handler() {
                this.currentPage = 1;
                this.loadOrders();
                this.fetchStats();
            },
        },
        activeQuickFilters() {
            this.currentPage = 1;
            this.loadOrders();
            this.fetchStats();
        },
    },

    created() {
        this.loadOrders();
        this.fetchStats();
    },

    methods: {
        refreshPage() {
            this.loadOrders();
            this.fetchStats();
        },
        async loadOrders() {
            this.isLoading = true;
            try {
                const res = await this.axios.get('/dashboard/orders/list', {
                    params: {
                        page: this.currentPage,
                        per_page: this.perPage,
                        filters: this.filters,
                        quickFilters: this.activeQuickFilters,
                    },
                });
                this.allOrders = res.data.orders || [];
                this.totalCount = res.data.total || 0;
            } catch {
                this.$bvToast.toast('Failed to load orders.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.isLoading = false;
            }
        },
        async fetchStats() {
            this.isStatsLoading = true;
            try {
                const res = await this.axios.get('/dashboard/orders/stats', {
                    params: {
                        filters: this.filters,
                        quickFilters: this.activeQuickFilters,
                    },
                });
                this.stats.orderCount = res.data.orderCount;
                this.stats.totalRevenue = res.data.totalRevenue;
                this.stats.avgOrderValue = res.data.avgOrderValue;
                this.stats.homeschoolCount = res.data.homeschoolCount;
                this.stats.totalUnits = res.data.totalUnits;
            } catch {
                this.$bvToast.toast('Failed to load order stats.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.isStatsLoading = false;
            }
        },

        applyDatePreset(preset) {
            const today = new Date();
            const pad = n => String(n).padStart(2, '0');
            const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
            const y = today.getFullYear();
            const m = today.getMonth();

            const ranges = {
                all:        [null, null],
                today:      [fmt(today), fmt(today)],
                this_week:  [fmt(new Date(today.setDate(today.getDate() - today.getDay()))), fmt(new Date())],
                this_month: [`${y}-${pad(m+1)}-01`, fmt(new Date())],
                last_month: [
                    fmt(new Date(y, m - 1, 1)),
                    fmt(new Date(y, m, 0)),
                ],
                this_year:  [`${y}-01-01`, fmt(new Date())],
                last_year:  [`${y-1}-01-01`, `${y-1}-12-31`],
            };

            if (preset === 'custom' || !ranges[preset]) return;
            const [from, to] = ranges[preset];
            this.filters.dateFrom = from || '';
            this.filters.dateTo   = to   || '';
        },

        toggleQuickFilter(key) {
            const idx = this.activeQuickFilters.indexOf(key);
            if (idx === -1) this.activeQuickFilters.push(key);
            else this.activeQuickFilters.splice(idx, 1);
        },

        removeChip(chip) {
            if (chip.quickKey) {
                this.activeQuickFilters = this.activeQuickFilters.filter(k => k !== chip.quickKey);
            } else {
                if (chip.key in this.filters) this.filters[chip.key] = chip.key === 'search' ? '' : null;
            }
        },

        resetFilters() {
            const today = new Date();
            const pad = n => String(n).padStart(2, '0');
            const y = today.getFullYear();
            this.filters = {
                search: '',
                dateFrom: `${y}-01-01`,
                dateTo: `${y}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`,
                grade: null,
                topic: null,
                schoolYear: null,
                state: null,
            };
            this.activeQuickFilters = [];
            this.datePreset = 'this_year';
        },
    },
};
</script>

<style scoped lang="scss">
.all-orders-admin {
    padding: 1.5rem;
    font-size: .9rem;
}

.stat-card {
    transition: transform .15s;
    &:hover { transform: translateY(-2px); }
    .stat-icon { opacity: .85; }
}

.text-purple { color: #6f42c1 !important; }

.filter-card {
    .filter-section-label {
        font-size: .7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .06em;
        color: #adb5bd;
    }
}

.quick-filter-btn {
    border-radius: 2rem;
    font-size: .8rem;
}

.filter-chip {
    cursor: pointer;
    font-size: .75rem;
    padding: .3em .6em;
    border-radius: 2rem;
    &:hover { opacity: .85; }
}
</style>